// General Components
import { LayoutContentProps } from "components/Layout/LayoutContent";

// Permissions
import { CompoundPermission } from "context/Ability";

// Enums
import { ChartIds, ChartRouteNames } from "enums/chartEnums";

// LD Component
import Projection from "components/Projection/Projection";
import DomainLayout from "components/Domain/DomainLayout";
import AdvancedPeersSearch from "components/AdvancedPeersSearch";
import SetupCompany from "components/SetupCompany";
import Refinement from "components/Refinement";
import AccountManager from "components/AccountManager";
import { VcpLayout } from "components/Vcp/VcpLayout";
import { SearchPage } from "components/CompanySearch/SearchPage";
import { Projects } from "pages/Projects";
import { ProjectDashboard } from "pages/ProjectDashboard";
import { Brief } from "pages/Brief";
import { BriefReview } from "pages/BriefReview";
import { HubManagementNew } from "pages/HubManagementNew";
import HubManagement from "pages/HubManagement/HubManagement";
import { ClientHub } from "pages/ClientHub";
import { HubAddUsers } from "pages/HubAddUsers";
import { PaceLandingLayout, AssessmentLayout, ReportLayout } from "components/Pace";
import SetupWizard from "components/SetupWizard";
import { Companies, People } from "components/Talent";
import { UserManagement } from "pages/UserManagement";
import { ChooseYourHub } from "pages/ChooseYourHub";
import { AssessmentCentre } from "pages/AssessmentCentre";
import { BehaviouralComparison } from "pages/BehavioralComparison";
import { MyPace } from "pages/MyPace";
import { MyDashboard } from "pages/MyDashboard";
import { MyExperience } from "pages/MyExperience";
import { LeadershipDashboard } from "pages/LeadershipDashboard";

export type RoutesConfig = {
    path: string;
    element: JSX.Element;
    components?: LayoutContentProps;
    exact?: boolean;
    permissions: CompoundPermission[];
    parentRoute:
        | "/choose-your-hub"
        | "/leadership-dynamics"
        | "/pace"
        | "/talent"
        | "/account-management"
        | "/hub-management"
        | "/user-management"
        | "/leadership-dashboard";
};

export type TChartIds = `${ChartIds}`;
export type TChartRouteNames = `${ChartRouteNames}`;
export interface IChartsRoutes {
    key: TChartIds;
    route: TChartRouteNames;
}

export const chartsRoutes: IChartsRoutes[] = [
    { key: ChartIds.Balance, route: ChartRouteNames.Balance },
    { key: ChartIds.Positioning, route: ChartRouteNames.Positioning },
    { key: ChartIds.Behavioural, route: ChartRouteNames.Behavioural },
    { key: ChartIds.BehaviouralBreakdown, route: ChartRouteNames.BehaviouralBreakdown },
    { key: ChartIds.Tenure, route: ChartRouteNames.Tenure },
    { key: ChartIds.Potential, route: ChartRouteNames.Potential },
    { key: ChartIds.Comparison, route: ChartRouteNames.Comparison },
    { key: ChartIds.Situational, route: ChartRouteNames.Situational },
    { key: ChartIds.Domain, route: ChartRouteNames.Domain },
    { key: ChartIds.Functional, route: ChartRouteNames.Functional },
    { key: ChartIds.Overall, route: ChartRouteNames.Overall },
    { key: ChartIds.Bridge, route: ChartRouteNames.Bridge },
];

export const routes: RoutesConfig[] = [
    // SETUP route
    {
        path: "/setup",
        element: <SetupWizard />,
        permissions: [
            { list: ["PACE", "SAAS_PACE"], operator: "or" },
            { list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT", "SAAS_PACE"], operator: "or" },
        ],
        parentRoute: "/pace",
    },
    // SELECT HUB route
    {
        path: "/",
        exact: true,
        element: <ChooseYourHub />,
        permissions: [
            { list: ["PACE", "SAAS_PACE"], operator: "or" },
            { list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT", "SAAS_PACE"], operator: "or" },
        ],
        parentRoute: "/choose-your-hub",
    },
    // LD Routes
    {
        path: "/search",
        element: <SearchPage />,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_COMPANY"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/my-searches",
        element: <Projects />,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_SEARCH_SAVED_OWN", "LD_SEARCH_SAVED_GRP"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_VIEW_GRP", "SAAS_LD_PROJECT_SEARCH_GRP"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/dashboard/:projectId",
        element: <ProjectDashboard />,
        permissions: [
            { list: ["LD_SEARCH_COMPANY", "LD_PROJECT_VIEW"], operator: "and" },
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/domain/:companyId",
        element: <DomainLayout />,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TALENT", "LD_PROJECT_EDIT"],
                operator: "or",
            },
            { list: ["SAAS_LD_PROJECT"], operator: "or" },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/advanced-peers-search/:companyId",
        element: <AdvancedPeersSearch />,
        permissions: [
            {
                list: ["LD_FREE", "LD_BASIC", "LD_LIMITED", "LD_TALENT", "LD_PROJECT_EDIT"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/team/:companyId",
        element: <SetupCompany />,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_TEAM", "LD_TALENT", "LD_PROJECT_EDIT"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/vcp/:companyId",
        element: <VcpLayout />,
        permissions: [
            {
                list: ["LD_TEAM", "LD_PROJECT_EDIT"],
                operator: "or",
            },
            {
                list: ["SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/insights/:companyId",
        element: <Projection />,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/insights/:companyId/:chartName",
        element: <Projection />,
        permissions: [
            {
                list: ["LD_FREE", "LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"],
                operator: "or",
            },
        ],
        exact: true,
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/brief/:companyId",
        element: <Brief />,
        permissions: [
            { list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_BRIEF_CREATE"], operator: "or" },
            { list: ["LD_TALENT"], operator: "or" },
        ],
        exact: true,
        parentRoute: "/leadership-dynamics",
    },
    {
        path: "/brief/review/:companyId",
        element: <BriefReview />,
        permissions: [
            { list: ["SAAS_LD_PROJECT", "SAAS_LD_PROJECT_BRIEF_CREATE"], operator: "or" },
            { list: ["LD_TALENT"], operator: "or" },
        ],
        exact: true,
        parentRoute: "/leadership-dynamics",
    },
    // SEARCH AI routes
    {
        path: "/companies/:companyId",
        element: <Companies />,
        permissions: [
            { list: ["LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT_SEARCH_AI_COMPANY"], operator: "or" },
        ],
        parentRoute: "/talent",
    },
    {
        path: "/people/:personId",
        element: <People />,
        permissions: [
            { list: ["LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT_SEARCH_AI_PEOPLE"], operator: "or" },
        ],
        parentRoute: "/talent",
    },
    {
        path: "/refinement/:companyId",
        element: <Refinement />,
        permissions: [
            { list: ["LD_TALENT"], operator: "or" },
            { list: ["SAAS_LD_PROJECT_SEARCH_AI_REFINE"], operator: "or" },
        ],
        parentRoute: "/talent",
    },
    // PACE Routes
    {
        path: "/my-dashboard",
        element: <MyDashboard />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_DASHBOARD"], operator: "and" },
        ],
        parentRoute: "/pace",
    },
    {
        path: "/my-pace",
        element: <MyPace />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PACE_REPORT"], operator: "and" },
        ],
        parentRoute: "/pace",
    },
    {
        path: "/my-experience",
        element: <MyExperience />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_INSIGHTS"], operator: "and" },
        ],
        parentRoute: "/pace",
    },
    {
        path: "/my-insights/:chartName",
        element: <MyExperience />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_INSIGHTS"], operator: "and" },
        ],
        exact: true,
        parentRoute: "/pace",
    },
    {
        path: "/assessment",
        element: <PaceLandingLayout />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN", "PACE_REPORT_RUN_GRP"], operator: "or" },
            { list: ["SAAS_PACE", "SAAS_PACE_ASSESSMENT"], operator: "and" },
        ],
        parentRoute: "/pace",
    },
    {
        path: "/assessment/:assessment_id",
        element: <AssessmentLayout />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT", "PACE_REPORT_RUN_OWN"], operator: "and" },
            { list: ["SAAS_PACE", "SAAS_PROFILE", "SAAS_PROFILE_VIEW_DASHBOARD"], operator: "and" },
        ],
        parentRoute: "/pace",
    },
    {
        path: "/report/user/:userId/assessment/:userAssessmentId",
        element: <ReportLayout />,
        permissions: [
            { list: ["PACE", "PACE_ASSESSMENT"], operator: "and" },
            { list: ["PACE_REPORT_RUN_OWN", "PACE_REPORT_RUN_GRP"], operator: "or" },
            { list: ["SAAS_PACE", "SAAS_PACE_ASSESSMENT"], operator: "and" },
        ],
        parentRoute: "/pace",
    },
    {
        path: "/admin",
        element: <AssessmentCentre />,
        permissions: [{ list: ["SYS_ADMIN_VIEW_PACE_PORTAL"], operator: "or" }],
        parentRoute: "/pace",
    },
    {
        path: "/behavioural-comparison",
        element: <BehaviouralComparison />,
        permissions: [{ list: ["SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"], operator: "or" }],
        parentRoute: "/pace",
    },
    // ACCOUNT MANAGER routes
    {
        path: "/settings",
        element: <AccountManager />,
        permissions: [
            {
                list: ["LD_FREE", "LD_BASIC", "LD_LIMITED", "LD_TALENT"],
                operator: "or",
            },
            { list: ["PACE", "SAAS_PACE"], operator: "or" },
        ],
        parentRoute: "/account-management",
    },
    {
        path: "/privacy",
        element: <AccountManager />,
        permissions: [
            {
                list: ["LD_FREE", "LD_BASIC", "LD_LIMITED", "LD_TALENT"],
                operator: "or",
            },
            { list: ["PACE", "SAAS_PACE"], operator: "or" },
        ],
        parentRoute: "/account-management",
    },
    // HUB MANAGEMENT routes
    {
        path: "/",
        element: <HubManagement />,
        permissions: [{ list: ["SYS_ADMIN_VIEW_CLIENT_HUB"], operator: "or" }],
        exact: true,
        parentRoute: "/hub-management",
    },
    {
        path: "/new",
        element: <HubManagementNew />,
        permissions: [{ list: ["SYS_ADMIN_VIEW_CLIENT_HUB"], operator: "or" }],
        exact: true,
        parentRoute: "/hub-management",
    },
    {
        path: "/client/:org_id",
        element: <ClientHub />,
        permissions: [{ list: ["SYS_ADMIN_VIEW_CLIENT_HUB"], operator: "or" }],
        exact: true,
        parentRoute: "/hub-management",
    },
    {
        path: "/add-users/:org_id",
        element: <HubAddUsers />,
        permissions: [{ list: ["SYS_ADMIN_VIEW_CLIENT_HUB"], operator: "or" }],
        exact: true,
        parentRoute: "/hub-management",
    },
    // USER MANAGEMENT routes
    {
        path: "/",
        element: <UserManagement />,
        permissions: [{ list: ["SYS_ADMIN_VIEW_USER_MANAGEMENT"], operator: "or" }],
        exact: true,
        parentRoute: "/user-management",
    },
    // LEADERSHIP DASHBOARD routes
    {
        path: "/overview/:companyId",
        element: <LeadershipDashboard />,
        permissions: [
            { list: ["LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"], operator: "or" },
        ],
        exact: true,
        parentRoute: "/leadership-dashboard",
    },
    {
        path: "/management/:companyId",
        element: <LeadershipDashboard />,
        permissions: [
            { list: ["LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"], operator: "or" },
        ],
        exact: true,
        parentRoute: "/leadership-dashboard",
    },
    {
        path: "/path-to-improvement/:companyId/:chartName",
        element: <LeadershipDashboard />,
        permissions: [
            { list: ["LD_LIMITED", "LD_BASIC", "LD_PROJECT_EDIT", "LD_TALENT", "SAAS_LD_PROJECT"], operator: "or" },
        ],
        exact: true,
        parentRoute: "/leadership-dashboard",
    },
];
